import React from 'react';
import { clsx } from 'clsx';
import { TZDate } from '@date-fns/tz';

import { format } from '../../utils/date';
import { gettext } from '../../utils/text';
import reverseUrl from '../../utils/urls';
import { Show, ShowPriceZone, SpecialPrice } from './types';

const CURRENCY = ' €';

const displayPrice = (price: number): string =>
    price % 1 === 0 ? price.toFixed(0) : price.toFixed(2);

const numberSorter = (a: number, b: number): number => a - b;

// Special prices and zone prices both render as "min € – max €", or a single
// price when every tier costs the same.
const formatPriceRange = (
    price: ShowPriceZone | null,
    specialPrices: SpecialPrice[],
): string => {
    let prices: number[] = [];
    if (specialPrices.length) {
        prices = specialPrices.map(sp => parseFloat(String(sp.price)));
    } else if (price) {
        const { green_price, yellow_price, red_price, blue_price } = price;
        prices = [green_price, yellow_price, red_price, blue_price].map(p =>
            parseFloat(String(p)),
        );
    }
    prices = prices.filter(p => !Number.isNaN(p)).sort(numberSorter);
    if (!prices.length) {
        return '';
    }
    const min = prices[0];
    const max = prices[prices.length - 1];
    if (min === max) {
        return `${displayPrice(min)}${CURRENCY}`;
    }
    return `${displayPrice(min)}${CURRENCY} – ${displayPrice(max)}${CURRENCY}`;
};

const formatDuration = (minutes: number | null | undefined): string => {
    if (!minutes) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours && mins) return `${hours} h ${mins} min`;
    if (hours) return `${hours} h`;
    return `${mins} min`;
};

/**
 * "Split" — PoC mockup
 *
 * Three-panel horizontal card:
 *   - Left:   visual of the performance
 *   - Middle: date/time + marks, name, genre, venue, duration, price, summary
 *   - Right:  primary "Buy ticket" CTA + intermission order link
 */
const SingleShowSplit = ({ show }: { show: Show }) => {
    const tallinnDate = new TZDate(show.time.date, 'Europe/Tallinn');

    const genres = show.staging.staging_note
        ? [show.staging.staging_note]
        : show.staging.types_of_staging;
    const genreText = genres.filter(Boolean).join(', ');

    const priceText = formatPriceRange(show.price, show.special_prices);
    const durationText = formatDuration(show.staging.length);

    const description = show.staging.summary?.trim();
    const imageUrl = show.staging.wide_image || show.staging.image;

    // Sold out / cancelled take the place of the buy link (see actions), so
    // the marks only carry urgency and occasion. A show that has already
    // started reads as sold out, same as on the staging page (show_row.html).
    const unavailableText = show.cancelled
        ? gettext('Cancelled')
        : show.sold_out || show.has_already_played
        ? gettext('Sold out')
        : null;
    const marks: { label: string; variant: string }[] = [];
    if (show.last_tickets && !unavailableText) {
        marks.push({ label: gettext('Last tickets'), variant: 'urgent' });
    }
    if (show.premiere) {
        marks.push({ label: gettext('Premiere'), variant: 'accent' });
    } else if (show.staging.guest_staging) {
        marks.push({ label: gettext('Guest show'), variant: 'neutral' });
    }
    if (show.excursion) {
        marks.push({ label: `+ ${gettext('Excursion')}`, variant: 'neutral' });
    }

    // ticket_link already prefers the performance's own link over the
    // staging default (Show.ticket_link on the backend). Without one there is
    // nothing to buy here; the card itself links to the show page.
    const buyHref = show.ticket_link;
    const showIntermission =
        show.has_intermission_order_open &&
        !show.has_intermission_order_sold_out &&
        !show.cancelled;
    // Appended to repeated link labels so screen readers can tell cards apart.
    const when = format(tallinnDate, 'EEEE, d MMMM HH:mm');
    const linkContext = `: ${show.staging.name}, ${when}`;

    return (
        <li
            className={clsx('show-split', {
                'show-split--sold-out':
                    (show.sold_out || show.has_already_played) &&
                    !show.cancelled,
                'show-split--cancelled': show.cancelled,
            })}
        >
            {/* Left: image */}
            <div className="show-split__image-panel">
                {/* Decorative: the title link already names the show. */}
                <div
                    className="show-split__image"
                    aria-hidden="true"
                    style={
                        imageUrl
                            ? { backgroundImage: `url(${imageUrl})` }
                            : undefined
                    }
                />
            </div>

            {/* Middle: info */}
            <div className="show-split__content">
                <div className="show-split__datetime">
                    <span className="show-split__date">
                        {format(tallinnDate, 'EEEE, dd MMMM yyyy')}
                    </span>{' '}
                    <span className="show-split__time">
                        {format(tallinnDate, 'HH:mm')}
                    </span>
                    {marks.map(mark => (
                        <span
                            key={mark.label}
                            className={clsx(
                                'show-split__mark',
                                `show-split__mark--${mark.variant}`,
                            )}
                        >
                            {mark.label}
                        </span>
                    ))}
                </div>

                <h3 className="show-split__title">
                    <a href={show.url} className="show-split__title-link">
                        {show.staging.name}
                        <span className="visually-hidden">, {when}</span>
                    </a>
                </h3>

                {genreText && (
                    <div className="show-split__genre">{genreText}</div>
                )}

                <ul className="show-split__meta">
                    {show.location && (
                        <li className="show-split__meta-item">
                            <span className="show-split__meta-label">
                                {gettext('Venue')}
                            </span>
                            <span className="show-split__meta-value">
                                {show.location.name}
                            </span>
                        </li>
                    )}
                    {durationText && (
                        <li className="show-split__meta-item">
                            <span className="show-split__meta-label">
                                {gettext('Duration')}
                            </span>
                            <span className="show-split__meta-value">
                                {durationText}
                            </span>
                        </li>
                    )}
                    {priceText && (
                        <li className="show-split__meta-item">
                            <span className="show-split__meta-label">
                                {gettext('Price')}
                            </span>
                            <span className="show-split__meta-value">
                                {priceText}
                            </span>
                        </li>
                    )}
                </ul>

                {description && (
                    <p className="show-split__description">{description}</p>
                )}
            </div>

            {/* Right: CTAs */}
            {(unavailableText || buyHref || showIntermission) && (
                <div className="show-split__actions">
                    {unavailableText && (
                        <span className="show-split__unavailable">
                            {unavailableText}
                        </span>
                    )}
                    {!unavailableText && buyHref && (
                        <a
                            href={buyHref}
                            className="btn btn-primary show-split__buy"
                        >
                            {gettext('Buy ticket')}
                            <span className="visually-hidden">
                                {linkContext}
                            </span>
                        </a>
                    )}
                    {showIntermission && (
                        <a
                            href={`${reverseUrl('intermission')}?show=${
                                show.id
                            }`}
                            className="show-split__intermission"
                        >
                            {gettext('Intermission order')}
                            <span className="visually-hidden">
                                {linkContext}
                            </span>
                        </a>
                    )}
                </div>
            )}
        </li>
    );
};

export default SingleShowSplit;
